import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import ServiceGrid from "@/pages/marketplace/ServiceGrid";
import PracticesInProgress from "@/pages/marketplace/PracticesInProgress";
import QuotesTable from "@/pages/marketplace/QuotesTable";
import RecapTable from "@/pages/marketplace/RecapTable";
import PropertyStage from "@/pages/marketplace/stages/PropertyStage";
import ChainedPrereqStage from "@/pages/marketplace/stages/ChainedPrereqStage";
import QuoteStage from "@/pages/marketplace/stages/QuoteStage";
import PurchaseStage from "@/pages/marketplace/stages/PurchaseStage";
import SlotPicker from "@/pages/marketplace/stages/SlotPicker";
import ShareLinkStage from "@/pages/marketplace/stages/ShareLinkStage";
import PurchaseModal from "@/pages/marketplace/PurchaseModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  mockUser, mockSubUser, mockProperties, mockService, mockServices, mockChainedServices,
  mockQuotes, mockInProgressPractices, mockRecap, mockShareLinkInfo,
  mockSlotMonth, buildMockMonthGrid, mockSlots,
} from "./mockData";

/**
 * Component Playground: rende tutti i componenti estratti in isolamento con dati fake.
 * Accessibile solo agli admin (`/dev/playground`). Nessuna chiamata API.
 * Stile: sidebar sinistra (lista storie) + main content (rendering). No lib esterna (no Storybook).
 */

const STORIES = [
  { id: "ServiceGrid", label: "ServiceGrid" },
  { id: "PracticesInProgress", label: "PracticesInProgress" },
  { id: "QuotesTable", label: "QuotesTable" },
  { id: "RecapTable", label: "RecapTable" },
  { id: "PropertyStage", label: "PropertyStage" },
  { id: "ChainedPrereqStage", label: "ChainedPrereqStage" },
  { id: "QuoteStage", label: "QuoteStage" },
  { id: "PurchaseStage", label: "PurchaseStage" },
  { id: "SlotPicker", label: "SlotPicker" },
  { id: "ShareLinkStage", label: "ShareLinkStage" },
  { id: "PurchaseModal", label: "PurchaseModal (full)" },
];

export default function Playground() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("ServiceGrid");

  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex gap-6 min-h-[80vh]" data-testid="playground-root">
      <aside className="w-56 shrink-0 border-r pr-4">
        <div className="label-eyebrow mb-3">Component Playground</div>
        <h2 className="font-display text-xl font-bold mb-4">Stories</h2>
        <nav className="space-y-1">
          {STORIES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setCurrent(s.id)}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                current === s.id
                  ? "bg-purple-100 text-purple-900 font-semibold"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
              data-testid={`playground-story-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="text-[10px] text-muted-foreground mt-6 leading-relaxed">
          Tutti i componenti sono renderizzati con <strong>dati fittizi</strong>.
          Nessuna chiamata API viene effettuata. Ideale per verificare visualmente
          modifiche di stile senza toccare i dati reali.
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <StoryHeader id={current} />
        <div className="bg-white rounded-lg border p-6" data-testid={`playground-render-${current}`}>
          <StoryRenderer id={current} />
        </div>
      </main>
    </div>
  );
}

function StoryHeader({ id }) {
  return (
    <div className="mb-4">
      <h1 className="font-display text-3xl font-bold">{id}</h1>
      <p className="text-xs text-muted-foreground mt-1">
        Import: <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">@/pages/marketplace/{importPathFor(id)}</code>
      </p>
    </div>
  );
}

function importPathFor(id) {
  if (["ServiceGrid", "PracticesInProgress", "QuotesTable", "RecapTable", "PurchaseModal"].includes(id)) {
    return id;
  }
  return `stages/${id}`;
}

function StoryRenderer({ id }) {
  switch (id) {
    case "ServiceGrid":
      return <ServiceGridStory />;
    case "PracticesInProgress":
      return <PracticesStory />;
    case "QuotesTable":
      return <QuotesStory />;
    case "RecapTable":
      return <RecapStory />;
    case "PropertyStage":
      return <PropertyStageStory />;
    case "ChainedPrereqStage":
      return <ChainedPrereqStory />;
    case "QuoteStage":
      return <QuoteStageStory />;
    case "PurchaseStage":
      return <PurchaseStageStory />;
    case "SlotPicker":
      return <SlotPickerStory />;
    case "ShareLinkStage":
      return <ShareLinkStory />;
    case "PurchaseModal":
      return <PurchaseModalStory />;
    default:
      return <p>Story non trovata.</p>;
  }
}

/* ============================ Stories ============================ */

function ServiceGridStory() {
  const [search, setSearch] = useState("");
  return (
    <ServiceGrid
      services={mockServices}
      user={mockUser}
      search={search}
      onSearchChange={setSearch}
      onSelect={(s) => alert(`Selected: ${s.name}`)}
    />
  );
}

function PracticesStory() {
  return <PracticesInProgress inProgress={mockInProgressPractices} properties={mockProperties} />;
}

function QuotesStory() {
  return (
    <QuotesTable
      quotes={mockQuotes}
      services={mockServices}
      onConvertToOrder={(q, svc) => alert(`Convert quote ${q._id} → ${svc.name}`)}
      onDelete={(q) => alert(`Delete quote ${q._id}`)}
    />
  );
}

function RecapStory() {
  const [expanded, setExpanded] = useState("2026-02");
  return <RecapTable recap={mockRecap} expandedMonth={expanded} onToggleMonth={setExpanded} />;
}

function PropertyStageStory() {
  const [form, setForm] = useState({ property_id: "" });
  const [propSearch, setPropSearch] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [newPropForm, setNewPropForm] = useState({
    address: "", city: "", province: "", zip_code: "",
    owner_first_name: "", owner_last_name: "", owner_phone: "",
  });
  return (
    <PropertyStage
      selected={mockService}
      properties={mockProperties}
      form={form}
      propSearch={propSearch}
      createMode={createMode}
      newPropForm={newPropForm}
      creatingProp={false}
      onPatchForm={(p) => setForm((f) => ({ ...f, ...p }))}
      onSetPropSearch={setPropSearch}
      onSetCreateMode={setCreateMode}
      onPatchNewPropForm={(p) => setNewPropForm((f) => ({ ...f, ...p }))}
      onSubmitNewProperty={() => alert("Submit new property")}
      onNext={() => alert("Next")}
    />
  );
}

function ChainedPrereqStory() {
  const [choices, setChoices] = useState({});
  return (
    <ChainedPrereqStage
      selected={mockService}
      chainedServicesDetails={mockChainedServices}
      chainedChoices={choices}
      onSetChoice={(id, choice) => setChoices((c) => ({ ...c, [id]: choice }))}
      onBack={() => alert("Back")}
      onNext={() => alert("Next")}
    />
  );
}

function QuoteStageStory() {
  const [quoteForm, setQuoteForm] = useState({ price_excl_vat: 150, vat_rate: 22, notes: "" });
  return (
    <QuoteStage
      selected={mockService}
      properties={mockProperties}
      form={{ property_id: "prop-1" }}
      quoteForm={quoteForm}
      generatingQuote={false}
      onPatchQuoteForm={(p) => setQuoteForm((q) => ({ ...q, ...p }))}
      onBack={() => alert("Back")}
      onGenerate={() => alert("Generate PDF (mock)")}
    />
  );
}

function PurchaseStageStory() {
  const [sharedMode, setSharedMode] = useState(false);
  const [asSubUser, setAsSubUser] = useState(false);
  const [subMarginPct, setSubMarginPct] = useState(10);
  const [form, setForm] = useState({
    property_id: "prop-1",
    is_shared_with_client: false,
    client_markup: 0,
  });

  const effForm = { ...form, is_shared_with_client: sharedMode };
  const activeUser = asSubUser ? mockSubUser : mockUser;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-md border">
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={sharedMode} onCheckedChange={setSharedMode} data-testid="story-toggle-shared" />
          <span>Modalità: condividi con cliente</span>
        </label>
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={asSubUser} onCheckedChange={setAsSubUser} data-testid="story-toggle-subuser" />
          <span>Simula sub-user (mostra pannello margine)</span>
        </label>
      </div>
      <PurchaseStage
        selected={mockService}
        properties={mockProperties}
        user={activeUser}
        isSubUser={asSubUser}
        form={effForm}
        subMarginPct={subMarginPct}
        visitOnFirstStep={false}
        slotMonth={mockSlotMonth()}
        monthGrid={buildMockMonthGrid()}
        selectedDate={null}
        slotsForSelectedDate={[]}
        chosenSlot={null}
        totalSlotsCount={0}
        onSubmit={(e) => { e.preventDefault(); alert("Submit (mock)"); }}
        onBackToPayment={() => alert("Back to payment_choice")}
        onBackToProperty={() => alert("Back to property")}
        onPatchForm={(p) => setForm((f) => ({ ...f, ...p }))}
        onSetSubMargin={setSubMarginPct}
        onSetSlotMonth={() => {}}
        onSetSelectedDate={() => {}}
        onSetChosenSlot={() => {}}
        onCancelShare={() => setSharedMode(false)}
      />
    </div>
  );
}

function SlotPickerStory() {
  const [slotMonth, setSlotMonth] = useState(mockSlotMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [chosenSlot, setChosenSlot] = useState(null);
  const monthGrid = buildMockMonthGrid();
  const slotsForSelectedDate = selectedDate
    ? mockSlots.map((s) => ({ ...s, date: selectedDate }))
    : [];
  return (
    <SlotPicker
      slotMonth={slotMonth}
      monthGrid={monthGrid}
      selectedDate={selectedDate}
      slotsForSelectedDate={slotsForSelectedDate}
      chosenSlot={chosenSlot}
      totalSlotsCount={monthGrid.filter((c) => c?.hasSlots).length * mockSlots.length}
      onSetSlotMonth={setSlotMonth}
      onSetSelectedDate={setSelectedDate}
      onSetChosenSlot={setChosenSlot}
    />
  );
}

function ShareLinkStory() {
  return (
    <ShareLinkStage
      shareLinkInfo={mockShareLinkInfo}
      onCopy={() => alert("Copied (mock)")}
      onClose={() => alert("Close")}
    />
  );
}

function PurchaseModalStory() {
  const [selected, setSelected] = useState(null);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          className="ht-gradient border-0"
          onClick={() => setSelected(mockService)}
          data-testid="story-open-modal"
        >
          Apri modale con servizio "APE" (mock)
        </Button>
        <Button
          variant="outline"
          onClick={() => setSelected({ ...mockService, __seedStage: "payment_choice" })}
          data-testid="story-open-modal-payment-choice"
        >
          Apri direttamente al bivio "Pagamento" (per demo split)
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        NB: il modale usa mock services/properties/user. Le chiamate API interne (workflow, availability,
        purchase) restituiranno errore o vuoto — è previsto in isolamento.
      </p>
      <PurchaseModal
        selected={selected}
        services={mockServices}
        properties={mockProperties}
        user={mockUser}
        openIntent={selected?.__seedStage ? { stage: selected.__seedStage, form: { property_id: mockProperties[0]?._id } } : null}
        onClose={() => setSelected(null)}
        onPropertiesChanged={async () => {}}
        onQuotesChanged={() => {}}
      />
    </div>
  );
}
